import React, { FC } from "react";
import { Button, Card, Col, Row, Typography, Upload, Form, Input, Collapse, Select, DatePicker } from "antd";
import styled from "@emotion/styled";
import { UploadOutlined } from "@ant-design/icons";
// import { getEmailFromSessionStorage } from "../utils/session_utils";
import useAutoPodderUploadHook from "./hooks/useAutoPodderUploadHook";
import { TaskRow } from "../zype/styled-components";
import { ComponentTitle, StledSpin } from "../custom-components";
import { TabComponentProps } from "../utils/common.interface";
import { integer } from "aws-sdk/clients/cloudfront";
import { Navigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";

const { Dragger } = Upload;

export const StyledDragger = styled(Dragger)`
	padding: 10px;
	& .ant-upload-drag {
		padding: 10px;
	}
`;

type PodderPramsFieldType = {
	weight_300?: integer;
	weight_180?: integer;
	weight_120?: integer;
	weight_90?: integer;
	weight_60?: integer;
	weight_45?: integer;
	weight_30?: integer;
	weight_15?: integer;
	max_cascade_seconds?: integer;
};

const weekDays = [
	{
		label: "Mo",
		value: "monday",
	},
	{
		label: "Tu",
		value: "tuesday",
	},
	{
		label: "We",
		value: "wednesday",
	},
	{
		label: "Th",
		value: "thursday",
	},
	{
		label: "Fr",
		value: "friday",
	},
	{
		label: "Sa",
		value: "saturday",
	},
		{
		label: "Su",
		value: "sunday",
	}
];

const AutoPodderComponent: FC<TabComponentProps> = () => {
	const {
		loading,
		autoPodderMode,
		setAutoPodderMode,
		handleUpload,
		uploadProps,
		redirectToBatchJobs,
		redirectToPreSeed,
		programs,
		selectedProgramId,
		setSelectedProgramId,
		weight_300,
		setWeight_300,
		weight_180,
		setWeight_180,
		weight_120,
		setWeight_120,
		weight_90,
		setWeight_90,
		weight_60,
		setWeight_60,
		weight_45,
		setWeight_45,
		weight_30,
		setWeight_30,
		weight_15,
		setWeight_15,
		max_cascade_seconds,
		setMax_cascade_seconds,
		startDate,
		setStartDate,
		selectedWeekDays,
		setSelectedWeekDays,
		noTrafficExcludedDays,
		setNoTrafficExcludedDays,
		requiredStartDayOfWeek,
		requiredStartDayOfWeekName,
		templateInfoLoading,
	} = useAutoPodderUploadHook();

	const handleDateChange = (date: Dayjs | null) => {
		if (date) {
			setStartDate(date);
		}
	};

	const handleWeekDaysSelect = (value: string) => {
		setSelectedWeekDays(prev =>
			prev.includes(value) ? prev.filter(day => day !== value) : [...prev, value]
		);
	};

	const handleNoTrafficExcludedDaysSelect = (value: string) => {
		setNoTrafficExcludedDays(prev =>
			prev.includes(value) ? prev.filter(day => day !== value) : [...prev, value]
		);
	};

	if (redirectToPreSeed) {
		return <Navigate to={`/auto-podder/preseed?batchJobId=${redirectToPreSeed}`} />;
	}

	if (redirectToBatchJobs) {
		return <Navigate to="/batch-jobs" />;
	}
	return (
		<StledSpin spinning={loading || templateInfoLoading}>
			<Row justify="center">
				<Col>
					<ComponentTitle>Upload Schedule & Structure to AutoPodder</ComponentTitle>
				</Col>
			</Row>
			<TaskRow>
				<Card
					actions={[
						<Button key="import-btn" type="primary" onClick={handleUpload}>
							Submit
						</Button>,
					]}
					style={{ width: "50%" }}
				>
					<StyledDragger {...uploadProps}>
						<Typography.Paragraph>Please select a CSV file</Typography.Paragraph>
						<Button icon={<UploadOutlined />}>Select File</Button>
					</StyledDragger>

					<Form.Item label="Type" name="type">
						<Select
							value={autoPodderMode}
							style={{ width: "100%" }}
							onChange={value => setAutoPodderMode(value)}
						>
							<Select.Option value="broadcast">Broadcast</Select.Option>
							<Select.Option value="syndication">Syndication</Select.Option>
						</Select>
					</Form.Item>

					<Form.Item
						label="Programs"
						name="autopodder_programs"
						style={{ display: autoPodderMode === "syndication" ? "block" : "none" }}
					>
						
					<Select
						key={`program-select-${autoPodderMode}`}
						showSearch
						placeholder="Select a program"
						value={selectedProgramId || undefined}
						onChange={(value) => {
							const programId = value || "";
							setSelectedProgramId(programId);
							// Reset start date when program changes
							if (value) {
								setStartDate(null);
							}
						}}
						allowClear
						filterOption={(input, option) => {
							const searchText = input.toLowerCase();
							const optionText = option?.children?.toString().toLowerCase() || "";
							return optionText.includes(searchText);
						}}
					>	
						{programs && programs.map(program => (
							<Select.Option key={program.id} value={program.id}>
								{program.title}
							</Select.Option>
						))}
					</Select>
					</Form.Item>

					<Form.Item
						label="Start Date"
						name="start_date"
						style={{ display: autoPodderMode === "syndication" ? "block" : "none" }}
					>
						<DatePicker
							value={startDate}
							onChange={handleDateChange}
							format="YYYY-MM-DD"
							placeholder={
								requiredStartDayOfWeekName
									? `Pick a ${requiredStartDayOfWeekName}`
									: "Select start date for structure file"
							}
							style={{ width: "100%" }}
							disabled={!selectedProgramId || selectedProgramId === ""}
							disabledDate={
								requiredStartDayOfWeek === null
									? undefined
									: (current: Dayjs) => current.day() !== requiredStartDayOfWeek
							}
						/>
						<Typography.Text type="secondary" style={{ display: "block", marginTop: "8px" }}>
							{!selectedProgramId || selectedProgramId === ""
								? "Please select a program first."
								: requiredStartDayOfWeekName
									? `Start date must be a ${requiredStartDayOfWeekName} — that's the day-of-week of the program template's first row. Other days are disabled to keep the week aligned during remap.`
									: "The structure file dates will be remapped to start from the selected date."}
						</Typography.Text>
					</Form.Item>
					<Form.Item
						label="Select Days"
						name="selected_days"
						style={{ display: autoPodderMode === "syndication" ? "block" : "none" }}
					>
						<div style={{ display: "flex", gap: "8px" }}>
							{weekDays.map(day => (
								<div
									key={day.value}
									onClick={() => handleWeekDaysSelect(day.value)}
									style={{
										padding: "6px 12px",
										border: "1px solid #d9d9d9",
										borderRadius: "6px",
										cursor: "pointer",
										background: selectedWeekDays.includes(day.value) ? "#1677ff" : "#fff",
										color: selectedWeekDays.includes(day.value) ? "#fff" : "#000",
									}}
								>
									{day.label}
								</div>
							))}
						</div>
					</Form.Item>
					<Form.Item
						label="Skip No-Traffic"
						name="no_traffic_excluded_days"
						style={{ display: autoPodderMode === "syndication" ? "block" : "none" }}
					>
						<div style={{ display: "flex", gap: "8px" }}>
							{weekDays.map(day => (
								<div
									key={day.value}
									onClick={() => handleNoTrafficExcludedDaysSelect(day.value)}
									style={{
										padding: "6px 12px",
										border: "1px solid #d9d9d9",
										borderRadius: "6px",
										cursor: "pointer",
										background: noTrafficExcludedDays.includes(day.value) ? "#ff4d4f" : "#fff",
										color: noTrafficExcludedDays.includes(day.value) ? "#fff" : "#000",
									}}
								>
									{day.label}
								</div>
							))}
						</div>
						<Typography.Text type="secondary" style={{ display: "block", marginTop: "8px" }}>
							Ads with no traffic (no ISCI on file) will not be podded on the selected days. Leave empty to allow them on every day.
						</Typography.Text>
					</Form.Item>

					<Collapse
						style={{ display: autoPodderMode === "broadcast" ? "block" : "none" }}
						items={[
							{
								key: "1",
								label: (
									<Row justify="center">
										<Col>
											<Typography.Title level={5}>Weights</Typography.Title>
										</Col>
									</Row>
								),
								children: (
									<>
										<Row justify="center">
											<Col>
												<Typography.Paragraph>
													Auto Podder Params: <br />
													Weights: Weight setting is done Ad Length wise. If left blank, default 100
													would be assumed. <br />
													Max Cascade Seconds: If left blank, default 60 Seconds would be assumed.
												</Typography.Paragraph>
											</Col>
										</Row>
										<Form.Item<PodderPramsFieldType> label="300" name="weight_300">
											{" "}
											<Input name="weight_300" value={weight_300} onChange={e => setWeight_300(e.target.value)} />{" "}
										</Form.Item>
										<Form.Item<PodderPramsFieldType> label="180" name="weight_180">
											{" "}
											<Input name="weight_180" value={weight_180} onChange={e => setWeight_180(e.target.value)} />{" "}
										</Form.Item>
										<Form.Item<PodderPramsFieldType> label="120" name="weight_120">
											{" "}
											<Input name="weight_120" value={weight_120} onChange={e => setWeight_120(e.target.value)} />{" "}
										</Form.Item>
										<Form.Item<PodderPramsFieldType> label="90" name="weight_90">
											{" "}
											<Input name="weight_90" value={weight_90} onChange={e => setWeight_90(e.target.value)} />{" "}
										</Form.Item>
										<Form.Item<PodderPramsFieldType> label="60" name="weight_60">
											{" "}
											<Input name="weight_60" value={weight_60} onChange={e => setWeight_60(e.target.value)} />{" "}
										</Form.Item>
										<Form.Item<PodderPramsFieldType> label="45" name="weight_45">
											{" "}
											<Input name="weight_45" value={weight_45} onChange={e => setWeight_45(e.target.value)} />{" "}
										</Form.Item>
										<Form.Item<PodderPramsFieldType> label="30" name="weight_30">
											{" "}
											<Input name="weight_30" value={weight_30} onChange={e => setWeight_30(e.target.value)} />{" "}
										</Form.Item>
										<Form.Item<PodderPramsFieldType> label="15" name="weight_15">
											{" "}
											<Input name="weight_15" value={weight_15} onChange={e => setWeight_15(e.target.value)} />{" "}
										</Form.Item>
										<Row justify="center">
											<Col>
												<Typography.Title level={5}>Max Cascade Seconds</Typography.Title>
											</Col>
										</Row>
										<Form.Item<PodderPramsFieldType>
											label="Max Cascade Seconds"
											name="max_cascade_seconds"
										>
											{" "}
											<Input name="max_cascade_seconds" value={max_cascade_seconds} onChange={e => setMax_cascade_seconds(e.target.value)} />{" "}
										</Form.Item>
									</>
								),
							},
						]}
					/>
				</Card>
			</TaskRow>
		</StledSpin>
	);
};

export default AutoPodderComponent;
